import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import OpsFlows from '../pages/OpsFlows';
import OpsSessions from '../pages/OpsSessions';
import OpsEcapAdmin from '../pages/OpsEcapAdmin';
import OpsAnalytics from '../pages/OpsAnalytics';
import OpsIntegration from '../pages/OpsIntegration';
import OpsApprovals from '../pages/OpsApprovals';
import OpsLive from '../pages/OpsLive';
import CompanyApplications from '../pages/CompanyApplications';
import { catalog } from '../applications/applicationCatalog';
import { getConsoleAccess, getAlerts, listTenants, activeTenant, setActiveTenant } from '../services/ecapApi';
import { ToastProvider } from '../ui/primitives';
import { IconBuilding, IconSparkles, IconUser, IconDollar, IconLock, IconCode } from '../ui/icons';
import './OpsApp.css';

// Movorra Studio — the purely-ECAP conversational-automation console, mounted ONLY at /studio. It runs on a Studio-pool
// session, or on a Movorra super-admin's single ride-share login (PLATFORM-SSO-1: App.js decides, ecapApi sends the
// matching token). This is SEPARATE from the ride-share OpsApp (Dispatch/Calendar/Drivers) that /r3ops and
// /r3dispatch render; the two never share a shell.
//   Run → Automation (flow designer + library), Leads (captured sessions), Analytics (funnels).
//   Platform → Admin (Movorra super-admin only: tenants, entitlements, billing, console access…).
// Which Run screens a company sees — and whether read-only — is governed per company by the
// Movorra admin's CONSOLE ACCESS policy (getConsoleAccess), enforced server-side too.
// Live and Approvals belong to `leads`: that is the feature ConsoleAccessInterceptor enforces for
// /studio/api/live and /studio/api/approvals, so the nav must hide/read-only them on the same setting.
const FEATURE_OF = { 'ops-flows': 'automation', 'ops-approvals': 'leads', 'ops-live': 'leads', 'ops-leads': 'leads', 'ops-analytics': 'analytics', 'ops-integration': 'integration' };

function buildNavItems(ecapAdmin, hotAlerts, access) {
  const lvl = (f) => (access && access[f]) || 'FULL';
  // Build = design + integrate; Operate = work the results. Each item is hideable per company (console access).
  const build = [];
  // The active company's applications (from the application catalog), so Movorra staff can reach them without
  // leaving Studio. Deliberately NOT in FEATURE_OF and never hidden: it carries only application names and
  // public routes (no keys, no secrets), and it guarantees a company whose other screens are all HIDDEN still
  // lands somewhere useful instead of an empty shell.
  build.push({ id: 'ops-applications', label: 'Applications', icon: <IconBuilding size={18} /> });
  if (lvl('automation') !== 'HIDDEN') build.push({ id: 'ops-flows', label: 'Automation', icon: <IconSparkles size={18} /> });
  if (lvl('integration') !== 'HIDDEN') build.push({ id: 'ops-integration', label: 'Integration', icon: <IconCode size={18} /> });
  const operate = [];
  if (lvl('leads') !== 'HIDDEN') operate.push({ id: 'ops-live', label: 'Live', icon: <IconUser size={18} /> });
  if (lvl('leads') !== 'HIDDEN') operate.push({ id: 'ops-approvals', label: 'Approvals', icon: <IconLock size={18} /> });
  if (lvl('leads') !== 'HIDDEN') operate.push({ id: 'ops-leads', label: 'Leads', icon: <IconUser size={18} />, badge: hotAlerts > 0 ? (hotAlerts > 99 ? '99+' : hotAlerts) : null });
  if (lvl('analytics') !== 'HIDDEN') operate.push({ id: 'ops-analytics', label: 'Analytics', icon: <IconDollar size={18} /> });
  const items = [];
  if (build.length) items.push({ section: 'Build', items: build });
  if (operate.length) items.push({ section: 'Operate', items: operate });
  // The ECAP admin console is visible ONLY to a Movorra super-admin — a plain tenant user never sees it.
  // The backend also 403s its endpoints, so this is defence-in-depth, not just cosmetic hiding.
  if (ecapAdmin) {
    items.push({ section: 'Platform', items: [
      { id: 'ecap-admin', label: 'Admin', icon: <IconBuilding size={18} /> },
    ]});
  }
  return items;
}

// `singleLogin`: Studio runs on the single company login (PLATFORM-SSO-1) — the Applications page then says company
// apps open without signing in again. `allowedScreens`: a company administrator's Studio (COMPANY-STUDIO-1, e.g. R3:
// Applications + Integration) — every other screen is left out of the nav and never rendered.
function StudioApp({ authSession, userName, onLogout, initialPage, singleLogin = false, allowedScreens = null }) {
  const allowed = (id) => !allowedScreens || allowedScreens.includes(id);
  const [activePage, setActivePage] = useState(initialPage || (allowedScreens ? allowedScreens[0] : 'ops-flows'));
  const [ecapAdmin, setEcapAdmin] = useState(false);
  const [access, setAccess] = useState(null); // { automation, leads, analytics } → HIDDEN|READ_ONLY|FULL
  const [hotAlerts, setHotAlerts] = useState(0);
  // The ONE global company context, set once in the sidebar and used across every screen (no per-page
  // re-selecting). A super-admin gets a switcher; a tenant user just sees their own company.
  const [activeCompany, setActiveCompany] = useState(activeTenant());
  const [companies, setCompanies] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const switchCompany = (id) => { setActiveTenant(id); setActiveCompany(id); };
  // A lead-notification email deep-links to …/studio?lead=<sessionId> — open the Leads view on arrival
  // (the SPA has no per-lead path route; the session id is in the email so the lead is easy to find).
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('lead')) setActivePage('ops-leads');
    } catch (e) { /* ignore */ }
  }, []);
  // Load the company's console-access policy (re-loads when the active company changes): it drives which
  // screens show / are read-only, and tells us if the caller is a Movorra super-admin.
  useEffect(() => {
    // STUDIOAPP-CONSOLE-ACCESS-RACE: on a rapid company switch two getConsoleAccess calls are in flight; without
    // a guard the SLOWER one can resolve last and set stale admin/access/companyName (and the wrong company's
    // tenant list) for the newly-selected company. Ignore any response after the effect has been superseded.
    let alive = true;
    getConsoleAccess(authSession).then((w) => {
      if (!alive) return;
      setEcapAdmin(!!w?.admin);
      setAccess(w?.access || null);
      setCompanyName(w?.name || w?.tenant || '');
      if (w?.admin) {
        // Super-admin: the company switcher drives which tenant they act as (X-Act-As-Tenant).
        listTenants(authSession).then((t) => { if (alive) setCompanies(t || []); }).catch(() => {});
      } else if (w?.tenant && activeTenant() !== w.tenant) {
        // Regular user: their Cognito identity (the JWT tenant) IS their company — reflect it in the UI and
        // outgoing headers, overriding the local default so the display matches the data the backend scopes.
        setActiveTenant(w.tenant); setActiveCompany(w.tenant);
      }
      // If the current landing screen is hidden for this company, fall back to the first visible one.
      const a = w?.access || {};
      const visible = ['ops-flows', 'ops-live', 'ops-approvals', 'ops-leads', 'ops-analytics', 'ops-integration'].filter((id) => a[FEATURE_OF[id]] !== 'HIDDEN' && allowed(id));
      // 'ops-applications' is the floor: it is never hidden, so a company with every other screen HIDDEN
      // lands there rather than on 'ecap-admin', which renders null for a non-admin (a blank console).
      setActivePage((cur) => (FEATURE_OF[cur] && a[FEATURE_OF[cur]] === 'HIDDEN' ? (visible[0] || 'ops-applications') : cur));
    }).catch(() => { if (alive) { setEcapAdmin(false); setAccess(null); } });
    return () => { alive = false; };
  }, [authSession, activeCompany]);
  // Poll the unacknowledged hot-lead alert count for the nav badge (light: count only, every 30s).
  useEffect(() => {
    if ((access && access.leads === 'HIDDEN') || !allowed('ops-leads')) return undefined; // no Leads screen → no badge to keep
    let alive = true;
    const tick = () => getAlerts(false, authSession).then((r) => { if (alive) setHotAlerts(r?.unacknowledged || 0); }).catch(() => {});
    tick();
    const h = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(h); };
  }, [activePage, access, authSession, activeCompany]); // eslint-disable-line react-hooks/exhaustive-deps
  const NAV_ITEMS = buildNavItems(ecapAdmin, hotAlerts, access)
    .map((section) => ({ ...section, items: section.items.filter((i) => allowed(i.id)) }))
    .filter((section) => section.items.length > 0);
  const activeLevel = (access && FEATURE_OF[activePage] && access[FEATURE_OF[activePage]]) || 'FULL';

  // Key pages on the active company so switching companies remounts them → they re-fetch for the new tenant.
  const renderPage = () => {
    const k = activeCompany || 'x';
    if (!allowed(activePage)) return null;
    if (activePage === 'ops-flows') return <OpsFlows key={k} authSession={authSession} readOnly={activeLevel === 'READ_ONLY'} />;
    // What the company's catalog says about its customer flow apps — so Leads/Approvals/Live can say where their
    // records come from and whether the feature is wired at all. { known: false } for a company without a
    // catalog: the pages then claim nothing.
    const sources = catalog.companyCapability(activeCompany, 'leads');
    if (activePage === 'ops-leads') return <OpsSessions key={k} authSession={authSession} readOnly={activeLevel === 'READ_ONLY'} recordSources={sources} />;
    if (activePage === 'ops-analytics') return <OpsAnalytics key={k} authSession={authSession} recordSources={sources} />;
    if (activePage === 'ops-integration') return <OpsIntegration key={k} authSession={authSession} readOnly={activeLevel === 'READ_ONLY'} />;
    if (activePage === 'ops-approvals') return <OpsApprovals key={k} authSession={authSession} readOnly={activeLevel === 'READ_ONLY'} capability={catalog.companyCapability(activeCompany, 'approvals')} />;
    if (activePage === 'ops-live') return <OpsLive key={k} authSession={authSession} capability={catalog.companyCapability(activeCompany, 'liveHandoff')} />;
    if (activePage === 'ops-applications') {
      // Scoped to the ACTIVE company: the catalog returns that company's definitions or [], never another's.
      const name = companyName || activeCompany;
      return (
        <div className="p-6">
          <CompanyApplications
            key={k}
            applications={catalog.listedApplicationsForCompany(activeCompany)}
            companyName={name}
            singleLogin={singleLogin}
            emptyMessage={`No applications are configured for ${name} yet.`}
          />
        </div>
      );
    }
    // Not keyed (a remount would drop the admin's current tab) — it re-scopes its per-company panels itself.
    if (activePage === 'ecap-admin') return ecapAdmin ? <OpsEcapAdmin authSession={authSession} activeCompany={activeCompany} /> : null;
    return null;
  };

  // Pages that render their own in-page header (and so opt out of the shared layout header).
  const SELF_HEADERED = ['ops-applications', 'ops-flows', 'ops-live', 'ops-approvals', 'ops-leads', 'ops-analytics', 'ops-integration', 'ecap-admin'];
  const showHeader = !SELF_HEADERED.includes(activePage);

  return (
    <ToastProvider>
      <div className="h-screen overflow-hidden flex bg-slate-50">
        <Sidebar
          items={NAV_ITEMS}
          activeItem={activePage}
          onItemChange={setActivePage}
          userName={userName}
          brandTitle="Movorra Studio"
          roleLabel={ecapAdmin ? 'Movorra admin' : 'Console'}
          onLogout={onLogout}
          companies={ecapAdmin ? companies : null}
          activeCompany={activeCompany}
          onCompanyChange={ecapAdmin ? switchCompany : null}
          companyName={companyName}
        />
        <main className="ops-main">
          {showHeader && (
            <div className="ops-page-header">
              <h2 className="ops-page-title">
                {(() => { const item = NAV_ITEMS.flatMap(g => g.items || [g]).find(i => i.id === activePage); return item ? item.label : ''; })()}
              </h2>
            </div>
          )}
          <div className="ops-content">
            {activeLevel === 'READ_ONLY' && (
              <div className="mx-6 mt-3 mb-1 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                <IconLock size={15} />
                <span>Read-only access — changes are disabled for your company by your Movorra administrator.</span>
              </div>
            )}
            {renderPage()}
          </div>
        </main>
      </div>
    </ToastProvider>
  );
}

export default StudioApp;
